/* eslint-disable camelcase */
import { CompositeScreenProps, useFocusEffect } from "@react-navigation/native"
import _ from "lodash"
import { observer } from "mobx-react-lite"
import moment from "moment"
import numeral from "numeral"
import { Spinner, Modal, View, Skeleton } from "native-base"
import React, { FC, memo, useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  Appearance,
  Dimensions,
  InteractionManager,
  Linking, RefreshControl,
  TextInput,
  useWindowDimensions,
  ViewStyle,
} from "react-native"
import {
  AutoImage,
  Button,
  FlatList,
  Input,
  JalaIcon,
  JalaLoading,
  LazyRender,
  ListEmptyProps,
  ListRenderItem,
  ListRenderItemInfo,
  ModelFormControl,
  Screen,
  Text,
} from "../../components"
import { Batch, Farm } from "../../database"
import { translate, TxKeyPath } from "../../i18n"
import { useStores } from "../../models"
import { BatchStoreModel } from "../../models/app-store/farm-store/batch-store/batch-store"
import { FarmStoreModel } from "../../models/app-store/farm-store/farm-store"
import { goBack, navigate } from "../../navigators"
import { color, nativeBaseTheme } from "../../theme"
import { delay } from "../../utils/delay"
import { InitialCycleSyncDialog } from "../home/farm-pond/farm-pond-list/farm-pond-list-screen"
import { SafeAreaView } from "react-native-safe-area-context"
import JalaAnalytic from "../../utils/jala-analytic"
import { getSnapshot } from "mobx-state-tree"

const empty = require("./empty-monitoring.png")
const windowWidth = Dimensions.get('window').width - 32;

const ROOT: ViewStyle = {
  flex: 1,
}

const FLEX_ROW: ViewStyle = {
  flexDirection: 'row',
  alignItems: 'center'
}

const CYCLE_HEADER: ViewStyle = {
  ...FLEX_ROW,
  paddingVertical: 10,
  paddingHorizontal: 18,
  justifyContent: 'space-between',
}

const DISCLAIMER: ViewStyle = {
  flexDirection: 'row',
  paddingHorizontal: 16,
  paddingVertical: 10,
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  backgroundColor: color.palette.orange300
}

const MONITORING_HEADER: ViewStyle = {
  ...FLEX_ROW,
  paddingHorizontal: 16,
  paddingVertical: 10,
  borderTopWidth: 1,
  borderTopColor: color.palette.gray200,
  backgroundColor: color.palette.gray100
}

const MONITORING_WRAPPER: ViewStyle = {
  ...FLEX_ROW,
  paddingHorizontal: 16,
  paddingVertical: 10,
  borderTopColor: color.palette.gray200,
  borderTopWidth: 1
}

const ICON_WRAPPER: ViewStyle = {
  backgroundColor: color.palette.orange500,
  borderRadius: 16 /2,
  width: 16,
  height: 16,
  padding: 1,
  marginRight: 3,
  alignItems: 'center'
}

const BORDER_BOTTOM: ViewStyle = {
  height: 4,
  backgroundColor: color.palette.gray200
}

const DROPDOWN_ICON: ViewStyle = {
  marginLeft: 20
}

const SEARCH_HEADER: ViewStyle = {
  ...FLEX_ROW,
  width: windowWidth,
}


const TEXT_FIELD: ViewStyle = {
  alignItems: 'center',
  justifyContent: 'center',
  flex: 1,
  marginVertical: 0,
  paddingVertical: 0,
  minHeight: 35
}

const BTN_CANCEL: ViewStyle = {
  marginLeft: 16,
}

const CONTENT_PLACEHOLDER: ViewStyle = {
  paddingHorizontal: 16
}

export const HeaderSearch: FC<CompositeScreenProps<any, any>> = observer(function HeaderSearch(props){
  const { navigation, route } = props;
  const { hidden } = route.params
  const inputRef = useRef<TextInput>()

  const handleChangeText = (text) => navigation.setParams({ text: text });
  const handleCancel = () => navigation.setParams({ hidden: !hidden, text: "" })
  return (
    <View style={SEARCH_HEADER}>
      <Input ref={inputRef} style={TEXT_FIELD} placeholderTx="searchPondPlaceholder" ml="16px" borderRadius="40px" px="12px" backgroundColor={color.palette.white} onChangeText={handleChangeText} />
      <Button testID="jala:id/cancel-button" preset="link" textPreset="title3" textColor={color.palette.white} style={BTN_CANCEL} onPress={handleCancel} tx="cancel" colorScheme="light" />
    </View>
  )
})

const CycleHeader = ({ cycle, index, hidden, onPress, handleNavigate, colorScheme }: { cycle?: any, index?: number, hidden?: boolean, handleNavigate?: () => void, onPress?: (index) => void, colorScheme?: "light" | "dark" }) => {
  const { pro, pond_name, age } = cycle;
  const icon = hidden ? 'expand_more' : 'expand_less';
  const colour = colorScheme === "dark" ? color.palette.white : nativeBaseTheme.colors.dark["50"];
  return (
    <View style={CYCLE_HEADER}>
      <View flex={1} marginRight="16px" style={FLEX_ROW}>
        <Text preset="title4" text={`${pond_name} (${age})`} colorScheme={colorScheme} mr="6px" numberOfLines={1} isTruncated color={color.palette.blueJalaDarker} />
        {pro && <View style={ICON_WRAPPER}><JalaIcon name="premium" size={14} color={color.palette.white} /></View> }
      </View>
      <View style={FLEX_ROW}>
        <Button testID="jala:id/see-pond-button" preset="link" tx="monitoring.seePond" textPreset="title4" colorScheme={colorScheme} onPress={handleNavigate} />
        <Button testID="jala:id/hide-button" preset="link" onPress={onPress} colorScheme={colorScheme} style={DROPDOWN_ICON}>
          <JalaIcon name={icon} size={25} color={colour} />
        </Button>
      </View>
    </View>
  )
}

const MonitoringItem = ({ data, label, colorScheme } : { data?: any, label?: string, colorScheme?: "light" | "dark" }) => {
  const { item: { cycle_actuals, age, cycle_predictions, total_seed } } = data;
  const cycleActual = cycle_actuals.find((c) => c[label] != null && c.age <= age);
  const actual = cycleActual && numeral(cycleActual[label]).format('0,0.[00]') || '-'
  const cyclePrediction = cycle_predictions.find((c) => c[label] != null && c.age <= age);
  const prediction = cyclePrediction && numeral(cyclePrediction[label]).format('0,0.[0]') || '-'
  const actualDoc = cycleActual && `${actual} (${cycleActual.age})` || '-'

  const value = useMemo(() => {
    if (label === "pond_populate") {
      const cycleActual = cycle_actuals.find((c) => c.age <= age && (c.feed > 0 || c.harvest > 0));
      const cyclePrediction = cycle_predictions.find((c) => c.age <= age && (c.feed > 0 || c.harvest > 0));

      const actual = cycleActual && numeral(cycleActual.total_tails * 100 / total_seed).format('0,0.[00]') || '-'
      const prediction = cyclePrediction && numeral(cyclePrediction.total_tails * 100 / total_seed).format('0,0.[0]') || '-'
      return {
        actual: cycleActual && `${actual} (${cycleActual.age})` || '-',
        prediction: prediction
      }
    }

    return {
      actual: actualDoc,
      prediction: prediction
    }
  }, [actualDoc, age, cycle_actuals, cycle_predictions, label, prediction, total_seed])

  return (
    <View style={MONITORING_WRAPPER}>
      <View flex={1}>
        <Text preset="body2" numberOfLines={2} tx={`monitoring.${_.camelCase(label)}` as TxKeyPath} colorScheme={colorScheme} isTruncated marginRight={1} />
      </View>
      <Text preset="body2" flex={1} text={value.actual} colorScheme={colorScheme} />
      <Text preset="body2" flex={1} text={value.prediction} colorScheme={colorScheme} />
    </View>
  )
}

const MonitoringContent = ({ data, colorScheme }: { data?: any, colorScheme?: "light" | "dark" }) => {
  return (
    <View>
      <View style={MONITORING_HEADER}>
        <Text preset="title4" flex={1} tx="monitoring.parameter" colorScheme={colorScheme} color={color.palette.gray800} isTruncated />
        <Text preset="title4" flex={1} tx="monitoring.actual" colorScheme={colorScheme} color={color.palette.gray800} isTruncated />
        <Text preset="title4" flex={1} tx="monitoring.estimation" colorScheme={colorScheme} color={color.palette.gray800} isTruncated />
      </View>
      <MonitoringItem data={data} label="weight" colorScheme={colorScheme} />
      <MonitoringItem data={data} label="size" colorScheme={colorScheme} />
      <MonitoringItem data={data} label="total_weight" colorScheme={colorScheme} />
      <MonitoringItem data={data} label="harvest_accumulation_weight" colorScheme={colorScheme} />
      <MonitoringItem data={data} label="adg" colorScheme={colorScheme} />
      <MonitoringItem data={data} label="feed_accumulation" colorScheme={colorScheme} />
      <MonitoringItem data={data} label="pond_populate" colorScheme={colorScheme} />
      <MonitoringItem data={data} label="survival_rate" colorScheme={colorScheme} />
      <MonitoringItem data={data} label="fcr" colorScheme={colorScheme} />
    </View>
  )
}

const EmptyState: FC<{colorScheme?: "light" | "dark", height?: string}> = (props: ListEmptyProps) => {
  const { colorScheme = Appearance.getColorScheme(), ...restProps } = props;
  return (
  <View flexGrow={1} minHeight="280px" alignItems="center" justifyContent="center" {...restProps}>
    <AutoImage source={empty} />
    <Text preset="title3" tx="monitoring.empty" colorScheme={colorScheme} />
  </View>
)}

const Header: FC<{ colorScheme?: "light"|"dark" }> = memo(observer(function Header(props) {
  const { colorScheme = "light" || Appearance.getColorScheme() } = props;
  const { appStore, appStore: { farmId, farmStore, loadingFarm } } = useStores()
  const { batchId, batchStore, initiating } = farmStore || FarmStoreModel.create();
  const { cycles, isFeedActualWithin4Days } = batchStore || BatchStoreModel.create();

  const onFarmChange = useCallback((value) => {
    return InteractionManager.runAfterInteractions(async () => {
      JalaAnalytic.log('Mobile app: Monitoring - change farm');

      appStore.set('farmId', value || null)
      if (appStore.farmStore) appStore.farmStore.initWithLastBatch(() => {
        if (appStore.farmStore.batchStore) appStore.farmStore.batchStore.initWithCycleActuals()
      })
    })
  }, [appStore])

  const onBatchChange = useCallback((value) => {
    return InteractionManager.runAfterInteractions(async () => {
      JalaAnalytic.log('Mobile app: Monitoring - change batch');

      appStore.farmStore.set('batchId', value)
      await delay(1)
      if (appStore.farmStore.batchStore) appStore.farmStore.batchStore.initWithCycleActuals()
    })
  }, [appStore.farmStore])

  const onPressInfo = useCallback(() => {
    JalaAnalytic.log('Mobile app: Monitoring - tap feedback');
    Linking.openURL('https://airtable.com/shrV5EikQh9JQ1hne').catch();
  }, [])

  return <>
    <ModelFormControl
      name="farm_id"
      colorScheme={colorScheme}
      formControlProps={{
        labelTx: "homeScreen.label",
        isDisabled: (loadingFarm || initiating)
      }}
      modelPickerProps={{
        Model: Farm,
        value: farmId,
        onValueChange: onFarmChange,
        isDisabled: (loadingFarm || initiating),
        InputRightElement: (loadingFarm || initiating) && <Spinner mr="9px"></Spinner>,
        isUseSearch: true
      }}
    />
    <ModelFormControl
      key={`batch_id-${farmId}`}
      name="batch_id"
      colorScheme={colorScheme}
      formControlProps={{
        labelTx: "monitoring.label",
        isDisabled: (loadingFarm || initiating || !batchId)
      }}
      modelPickerProps={{
        Model: Batch,
        dataLabel: "start_batch",
        fetchData: (model, search) => (
          model.fetch({
            farm_id__eq: farmId,
            sort: 'start_batch,desc',
          }).then((models) => models.filter((m) => JSON.stringify({ start_batch: moment(m.start_batch).format('DD MMMM YYYY') }).toLowerCase().includes(search.toLowerCase())))
        ),
        renderSelectedItemText: (item) => item && `${moment(item.start_batch).format("DD MMM YYYY")}`,
        value: !!batchId && batchId,
        isDisabled: (loadingFarm || initiating || !batchId),
        onValueChange: onBatchChange,
        InputRightElement: (loadingFarm || initiating) && <Spinner mr="9px"></Spinner>
      }}
    />
    {!!batchStore && cycles.length !== 0 && (
      <>
        <Text text={`${cycles.length} ${translate("monitoring.cyclesCount")}`} preset="title3" mt="16px" colorScheme={colorScheme} ml="16px" color={color.palette.gray800} />
        {!isFeedActualWithin4Days && (
          <View style={DISCLAIMER}>
            <JalaIcon name="info-1" size={16} color={color.palette.gray800} />
            <View ml="8px" flex={1}>
              <Text preset="body3" colorScheme={colorScheme}>
                <Text tx="monitoring.disclaimer" preset="body3" colorScheme={colorScheme} color={color.palette.gray800} />
                <Text text=" " preset="body3" />
                <Text tx="monitoring.feedback" preset="body3" colorScheme={colorScheme} color={color.primary} onPress={onPressInfo} />
              </Text>
            </View>
          </View>
        )}
      </>
    )}
  </>
}))

const Item: FC<{ colorScheme?: "light" | "dark", info: ListRenderItemInfo<any>, loading?: boolean, isHide?: Array<any>, handleHide?: (item: any) => void, handleNavigate?: (item: any) => void }> = observer(function Item(props) {
  const { colorScheme = "light" || Appearance.getColorScheme(), info, handleNavigate } = props;
  const { item:  { cycle_actuals, age, finished_at } } = info;
  const [showAll, setShowAll] = useState<boolean>(true);

  const date = moment(finished_at || undefined).subtract(4, 'days').format('YYYY-MM-DD');
  const showDisclaimer = age >= 4 && !cycle_actuals.find((c) => (
    c.date >= date && c.feed
  ));

  const onNavigate = useCallback(() => handleNavigate(info.item), [info.item, handleNavigate])
  const onHide = useCallback(() => {
    JalaAnalytic.log('Mobile app: Monitoring - Hide/show estimation');
    setShowAll(!showAll)
  }, [showAll])

  const renderPlaceholder = useCallback(() => {
    return <View style={CONTENT_PLACEHOLDER}>
      <Skeleton height="424px" marginY="8px" />
    </View>
  }, [])

  return <LazyRender renderPlaceholder={renderPlaceholder}>
    <View>
      <CycleHeader cycle={info.item} hidden={showAll} index={info.index} handleNavigate={onNavigate} onPress={onHide} colorScheme={colorScheme} />
      {showDisclaimer && <Text
        tx="overviewScreen.disclaimer"
        preset="body3"
        mb="6px" mx="16px"
        colorScheme={colorScheme}
        color={nativeBaseTheme.colors.danger["500"]}
      />}
      {showAll && <MonitoringContent data={info} colorScheme={colorScheme} />}
      <View style={BORDER_BOTTOM} />
    </View>
  </LazyRender>;
})

export const MonitoringScreen: FC<{ colorScheme?: "light" | "dark" } & CompositeScreenProps<any, any>> = observer(function MonitoringScreen(props) {
  const { colorScheme = "light" || Appearance.getColorScheme(), route: { params } } = props
  const { height } = useWindowDimensions();

  // Pull in one of our MST stores
  const { appStore: { farmStore }, homeStore, homeStore: { refreshing } } = useStores();

  const { initiating, batchStore } = farmStore || FarmStoreModel.create({});
  const { cycles, lastUpdateCycle, loading } = batchStore || BatchStoreModel.create({});

  const data = useMemo(() => (
    lastUpdateCycle && getSnapshot(cycles).filter((c) => (
      JSON.stringify(c).toLowerCase().includes((params?.text || '').toLowerCase())
    )) || []
  ), [cycles, lastUpdateCycle, params?.text]);

  useEffect(() => {
    if (!farmStore) return goBack('Home.Stack');
  }, [farmStore])

  useFocusEffect(useCallback(() => {
    InteractionManager.runAfterInteractions(() => {
      if (batchStore) batchStore.initWithCycleActuals();
    })
  }, [batchStore]))

  const onNavigate = useCallback((cycle) => {
    InteractionManager.runAfterInteractions(async () => {
      farmStore.set('pondId', cycle.pond_id)
      farmStore.pondStore.set('cycleId', cycle.id);
      JalaAnalytic.log('Mobile app: Monitoring - open pond page');
      await delay(1);
      // if (farmStore.pondStore.cycleStore.synced) {
        navigate('Home.Stack', "Home.Cycle.Stack")
      // } else {
      //   farmStore.pondStore.cycleStore.initAllSequential()
      //     .then(() => navigate('Home.Stack', "Home.Cycle.Stack"))
      // }
    })
  }, [farmStore])

  const renderItem: ListRenderItem<any> = useCallback(((info) => {
    return <Item
      info={info}
      loading={loading}
      handleNavigate={onNavigate}
      colorScheme={colorScheme}
    />
  }), [colorScheme, loading, onNavigate])

  const keyExtractor = useCallback((item, index) => `key-${index}`, [])

  return (<>
    <Screen
      style={ROOT}
      preset="fixed"
      unsafe
      colorScheme={colorScheme}
      backgroundBar={colorScheme === "light" ? color.primary : undefined}
      statusBar={colorScheme === "light" ? "light-content" : "dark-content"}
      hideOnBlurred={false}
    >
      <FlatList
        data={data}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        ListEmptyComponent={loading && (
          <View flexGrow={1} alignItems="center" justifyContent="center" minHeight={`${height - 360}px`}>
            <JalaLoading />
          </View>
        ) || <EmptyState colorScheme={colorScheme} height={`${height - 360}px`} />}
        ListHeaderComponent={<Header colorScheme={colorScheme} />}
        ListFooterComponent={<SafeAreaView edges={['bottom']} />}
        refreshControl={<RefreshControl
          refreshing={refreshing}
          onRefresh={homeStore.onRefreshBatch}
        />}
        estimatedItemSize={300}
        showsVerticalScrollIndicator={false}
      />
      <Modal isOpen={(refreshing || initiating)}>
        <View
          width="72px" height="72px"
          alignItems="center"
          justifyContent="center"
          rounded="48px"
          backgroundColor="rgba(255, 255, 255, 0.75)"
        >
          <JalaLoading />
        </View>
      </Modal>
    </Screen>
    <InitialCycleSyncDialog colorScheme={colorScheme} />
  </>)
})
